import React, { useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  Paper,
  Grid,
  InputAdornment,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { db } from "../utils/firebase";
import { notifyAdmins } from "../utils/adminNotify";
import { doc, setDoc } from "firebase/firestore";
import { getFriendlyAuthError } from "../utils/friendlyAuthError";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA",
  "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK",
  "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

export default function RegisterPage() {
  const { register, logout, sendVerificationEmail } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    dob: "",
    phone: "",
    city: "",
    state: "",
    gender: "",
    occupation: ""
    });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const cred = await register(form.email, form.password);
      await setDoc(doc(db, "users", cred.user.uid), {
        email: form.email,
        name: form.name,
        dob: form.dob,
        phone: form.phone,
        city: form.city,
        state: form.state,
        occupation: form.occupation || "",
        gender: form.gender,
        role: "user",
        approved: false,
        emailVerified: false,
        welcomeEmailSent: false,
        createdAt: new Date()
      });
      await sendVerificationEmail(cred.user);
      await logout();
      void notifyAdmins('user_registered', { userId: cred.user.uid, email: form.email, name: form.name });
      setSuccess("Verification email sent. Please verify your email before signing in.");
      navigate("/login", {
        state: {
          message: "Verification email sent. Please check your inbox before signing in.",
          email: form.email,
        },
      });
    } catch (err) {
      const codeOrMessage = err?.code || err?.message || "";
      setError(getFriendlyAuthError(codeOrMessage));
    }
    setSubmitting(false);
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#fafafa",
        m: 0,
        p: 0,
        py: { xs: 2, sm: 4, md: 6 },
      }}
    >
      <Container maxWidth="sm" sx={{ px: { xs: 1, sm: 2 } }}>
        <Paper elevation={4} sx={{ p: { xs: 2, sm: 4, md: 5 }, borderRadius: 4 }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <PersonAddAltOutlinedIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: "primary.main", mb: 1 }} />
            <Typography variant="h4" mb={2} sx={{ fontSize: { xs: 22, sm: 28 } }}>Create Account</Typography>
          </Box>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Full Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonAddAltOutlinedIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel id="gender-label">Gender</InputLabel>
                    <Select
                      labelId="gender-label"
                      id="gender"
                      name="gender"
                      value={form.gender}
                      label="Gender"
                      onChange={handleChange}
                    >
                      <MenuItem value="">Select gender</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date of Birth"
                  name="dob"
                  type="date"
                  value={form.dob}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarMonthOutlinedIcon />
                      </InputAdornment>
                    )
                  }}
                  sx={{ fontSize: { xs: 14, sm: 16 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIphoneOutlinedIcon />
                      </InputAdornment>
                    )
                  }}
                  sx={{ fontSize: { xs: 14, sm: 16 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnOutlinedIcon />
                      </InputAdornment>
                    )
                  }}
                  sx={{ fontSize: { xs: 14, sm: 16 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Occupation"
                  name="occupation"
                  value={form.occupation}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel id="state-label">State</InputLabel>
                  <Select
                    labelId="state-label"
                    id="state"
                    name="state"
                    value={form.state}
                    label="State"
                    onChange={handleChange}
                    sx={{ fontSize: { xs: 14, sm: 16 } }}
                  >
                    {US_STATES.map((abbr) => (
                      <MenuItem key={abbr} value={abbr}>
                        {abbr}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon />
                      </InputAdornment>
                    )
                  }}
                  sx={{ fontSize: { xs: 14, sm: 16 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{ fontSize: { xs: 14, sm: 16 } }}
                />
              </Grid>
            </Grid>
            {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              sx={{ mt: 3, mb: 1, fontSize: { xs: 15, sm: 17 } }}
              disabled={submitting}
            >
              Register
            </Button>
            <Button
              color="secondary"
              fullWidth
              onClick={() => navigate("/login")}
              sx={{ mt: 1, fontSize: { xs: 14, sm: 16 } }}
            >
              Already have an account? Login
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}